import { CommonModule } from '@angular/common';
import {
    Component, computed, ElementRef, EventEmitter, inject, Input, OnChanges,
    Output, signal, SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SchemaFormComponent } from '../schema-form/schema-form.component';
import { SchemaLeafEditorComponent } from '../schema-leaf-editor/schema-leaf-editor.component';

/**
 * Finder/Miller-columns–style schema browser.
 *
 * <p>Given a JSON-schema and a value, renders horizontally-stacked columns,
 * one per navigation level. The first column shows the root's properties.
 * Containers (nested objects, arrays of objects) in any column appear as
 * clickable <strong>nav rows</strong>; clicking a nav row opens the next
 * column to its right showing that container's contents. Leaf fields are
 * inline-editable in the column they live in.
 *
 * <p>Layout invariants:
 * <ul>
 *   <li>activePath is a list of segments — each either a property name
 *       ("location") or an array-index marker ("[0]"). One nav-click adds
 *       one segment.</li>
 *   <li>Selecting a nav row in column N truncates activePath to N and
 *       pushes the clicked segment, so opening a sibling closes the
 *       deeper-than-N branch automatically.</li>
 *   <li>Object/array-item columns reuse {@link SchemaFormComponent} in
 *       columnMode for their fields. Array columns are rendered inline
 *       (items as nav rows + Add button).</li>
 * </ul>
 *
 * <p>Value flow mirrors SchemaForm's: every edit propagates a cleansed
 * value out via {@code (valueChange)}, the per-row delete-icon emits
 * {@code (pathCleared)} with the full JSON path, and field focus emits
 * {@code (focusPath)} so the JSON viewer can highlight the matching node.
 */
@Component({
    selector: 'app-schema-column-browser',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule, MatIconModule, MatTooltipModule,
        SchemaFormComponent,
        SchemaLeafEditorComponent,
    ],
    templateUrl: './schema-column-browser.component.html',
    styleUrl: './schema-column-browser.component.css',
})
export class SchemaColumnBrowserComponent implements OnChanges {

    @Input({ required: true }) schema!: any;
    @Input() value: any = null;
    @Input() disabled = false;

    @Output() valueChange = new EventEmitter<any>();
    @Output() pathCleared = new EventEmitter<string>();
    @Output() focusPath = new EventEmitter<string>();

    private readonly el: ElementRef<HTMLElement> = inject(ElementRef);

    /** Open navigation path. Each segment is a property name or "[N]". */
    readonly activePath = signal<string[]>([]);

    /** Local master value — updated by user edits before propagating up. */
    readonly masterValue = signal<any>({});

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['value']) {
            const v = this.value;
            this.masterValue.set(v && typeof v === 'object' && !Array.isArray(v) ? v : {});
            // Trim activePath if the new value no longer covers it (e.g.,
            // value changed externally and a previously-open subtree is gone).
            this.trimInvalidPath();
        }
    }

    /** Walk activePath against the current value/schema and drop any
     *  segments that no longer point at a real subtree. */
    private trimInvalidPath(): void {
        const path = this.activePath();
        let curSchema = this.schema;
        let curValue: any = this.masterValue();
        let validLength = 0;
        for (const seg of path) {
            if (seg.startsWith('[')) {
                const idx = parseInt(seg.slice(1, -1), 10);
                if (!curSchema?.items || !Array.isArray(curValue) || isNaN(idx) || curValue[idx] === undefined) break;
                curSchema = curSchema.items;
                curValue = curValue[idx];
                validLength++;
            } else {
                const fs = curSchema?.properties?.[seg];
                if (!fs) break;
                curSchema = fs;
                curValue = curValue?.[seg];
                validLength++;
            }
        }
        if (validLength !== path.length) {
            this.activePath.set(path.slice(0, validLength));
        }
    }

    /** Column descriptors computed from activePath + schema + value. */
    readonly columns = computed<ColumnDescriptor[]>(() => {
        const cols: ColumnDescriptor[] = [];
        let curSchema = this.schema;
        let curValue: any = this.masterValue() ?? {};
        const curPath: string[] = [];

        cols.push({ schema: curSchema, value: curValue, path: [], kind: 'object' });

        for (const seg of this.activePath()) {
            if (seg.startsWith('[')) {
                const idx = parseInt(seg.slice(1, -1), 10);
                if (!curSchema?.items || !Array.isArray(curValue) || isNaN(idx)) break;
                curSchema = curSchema.items;
                curValue = curValue[idx] ?? {};
                curPath.push(seg);
                cols.push({ schema: curSchema, value: curValue, path: [...curPath], kind: 'object' });
            } else {
                const fieldSchema = curSchema?.properties?.[seg];
                if (!fieldSchema) break;
                const fieldValue = curValue?.[seg];
                curPath.push(seg);

                const isArrayOfObjects = fieldSchema.type === 'array'
                    && (fieldSchema['x-options']?.kind === 'objects'
                        || fieldSchema.items?.type === 'object');
                const isObjectContainer = fieldSchema.type === 'object'
                    && fieldSchema.properties;

                if (isArrayOfObjects) {
                    curSchema = fieldSchema;
                    curValue = Array.isArray(fieldValue) ? fieldValue : [];
                    cols.push({ schema: curSchema, value: curValue, path: [...curPath], kind: 'array' });
                } else if (isObjectContainer) {
                    curSchema = fieldSchema;
                    curValue = fieldValue ?? {};
                    cols.push({ schema: curSchema, value: curValue, path: [...curPath], kind: 'object' });
                } else {
                    // Leaf — scalar / boolean / enum / autocomplete / picker /
                    // array-of-primitives. Rightmost column shows the editor.
                    curSchema = fieldSchema;
                    curValue = fieldValue;
                    cols.push({ schema: curSchema, value: curValue, path: [...curPath], kind: 'leaf' });
                }
            }
        }
        return cols;
    });

    /** The segment activePath uses to navigate OUT of column `columnIndex`,
     *  so the matching nav row in that column gets highlighted. */
    selectedSegmentFor(columnIndex: number): string | null {
        return this.activePath()[columnIndex] ?? null;
    }

    /** A nav row in column `columnIndex` was clicked — open the next column. */
    onOpenPath(columnIndex: number, segment: string): void {
        const newPath = [...this.activePath().slice(0, columnIndex), segment];
        this.activePath.set(newPath);
        this.focusPath.emit(this.pathToJsonPath(newPath));
        requestAnimationFrame(() => this.scrollColumnsToEnd());
    }

    /** Public API — drive the activePath from the outside (host calls this
     *  when the JSON viewer's nodeClick fires). Trims to whatever subtree
     *  is actually reachable. */
    goTo(segments: string[]): void {
        // Reach as far as the schema/value lets us — anything beyond gets
        // truncated by trimInvalidPath() on the next tick.
        this.activePath.set([...segments]);
        this.trimInvalidPath();
        this.focusPath.emit(this.pathToJsonPath(this.activePath()));
        requestAnimationFrame(() => this.scrollColumnsToEnd());
    }

    /** Header trash icon — delete the JSON value at this column's path.
     *  Closes the column (and any deeper ones) optimistically; the host's
     *  deleteAtPath updates the JSON, which propagates back and trims the
     *  path via trimInvalidPath. Root column (path=[]) can't be deleted;
     *  read-only leaves can't either. */
    deleteColumn(columnIndex: number, ev?: Event): void {
        ev?.stopPropagation();
        const col = this.columns()[columnIndex];
        if (!col || !this.canDeleteColumn(col)) return;
        this.pathCleared.emit(this.columnPath(col));
        // Close this column + any deeper ones immediately. The segment that
        // opened this column lives at index (columnIndex - 1) in activePath.
        this.activePath.set(this.activePath().slice(0, columnIndex - 1));
    }

    /** True when the column's path can be deleted — drives the visibility
     *  of the header trash button. Root is locked, read-only leaves are
     *  locked, and empty values have nothing to delete. */
    canDeleteColumn(col: ColumnDescriptor): boolean {
        if (col.path.length === 0 || this.disabled) return false;
        if (col.schema?.readOnly === true) return false;
        const v = col.value;
        if (v === null || v === undefined) return false;
        if (typeof v === 'string' && v === '') return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') {
            for (const k of Object.keys(v)) {
                const x = v[k];
                if (x !== null && x !== '' && x !== undefined
                    && !(Array.isArray(x) && x.length === 0)) {
                    return true;
                }
            }
            return false;
        }
        return true;
    }

    /** True when an array column allows appending new items. Mirrors
     *  canDeleteColumn's readOnly + disabled gates. */
    canAddToColumn(col: ColumnDescriptor): boolean {
        if (col.kind !== 'array' || this.disabled) return false;
        if (col.schema?.readOnly === true) return false;
        return true;
    }

    /** Tooltip text for the column header's trash button, varies by kind. */
    deleteTooltip(col: ColumnDescriptor): string {
        if (col.kind === 'array') return 'Delete this array';
        if (col.kind === 'leaf') return 'Clear value';
        return 'Delete this branch';
    }

    /** LeafEditor emitted a new scalar value for a 'leaf' column. Writes
     *  the scalar at col.path directly — no object-merge complications. */
    onLeafValueChange(columnIndex: number, scalar: any): void {
        const col = this.columns()[columnIndex];
        if (!col || col.kind !== 'leaf') return;
        const existing = this.getAtPath(this.masterValue(), col.path);
        if (this.deepEqual(existing, scalar)) return;
        const next = this.deepClone(this.masterValue());
        this.setAtPath(next, col.path, scalar);
        this.masterValue.set(next);
        this.valueChange.emit(this.cleanseValue(next));
    }

    /** SchemaForm in columnMode emitted a new value for column N's slice. */
    onColumnValueChange(columnIndex: number, newValue: any): void {
        const col = this.columns()[columnIndex];
        if (!col) return;

        // Loop guard — if the value at this path already deep-equals the
        // incoming one (SchemaForm bounced its own applyInitialValue back
        // at us), skip the update. Otherwise we'd round-trip forever.
        const existing = this.getAtPath(this.masterValue(), col.path);
        if (this.deepEqual(this.cleanseValue(existing), this.cleanseValue(newValue))) {
            return;
        }

        const next = this.deepClone(this.masterValue());
        if (col.path.length === 0) {
            // Root level — replace top-level keys with the new value but
            // preserve any keys the form didn't render (defensive).
            for (const k of Object.keys(newValue ?? {})) {
                next[k] = newValue[k];
            }
        } else {
            this.setAtPath(next, col.path, newValue ?? {});
        }
        this.masterValue.set(next);
        this.valueChange.emit(this.cleanseValue(next));
    }

    /** Array column — append a fresh empty item + open it. */
    addArrayItem(columnIndex: number): void {
        const col = this.columns()[columnIndex];
        if (!col || col.kind !== 'array') return;
        const arr = Array.isArray(col.value) ? col.value : [];
        const newArr = [...arr, {}];
        const next = this.deepClone(this.masterValue());
        this.setAtPath(next, col.path, newArr);
        this.masterValue.set(next);
        this.valueChange.emit(this.cleanseValue(next));
        const newIdx = newArr.length - 1;
        const newPath = [...this.activePath().slice(0, columnIndex), `[${newIdx}]`];
        this.activePath.set(newPath);
        this.focusPath.emit(this.pathToJsonPath(newPath));
        requestAnimationFrame(() => this.scrollColumnsToEnd());
    }

    /** Array column — remove item at index. */
    removeArrayItem(columnIndex: number, index: number, ev?: Event): void {
        ev?.stopPropagation();
        const col = this.columns()[columnIndex];
        if (!col || col.kind !== 'array') return;
        const arr = Array.isArray(col.value) ? col.value : [];
        const newArr = arr.filter((_: any, i: number) => i !== index);
        const next = this.deepClone(this.masterValue());
        this.setAtPath(next, col.path, newArr);
        this.masterValue.set(next);
        this.valueChange.emit(this.cleanseValue(next));
        // If the removed item was the one opened in the next column, close
        // that column.
        const removedSeg = `[${index}]`;
        if (this.activePath()[columnIndex] === removedSeg) {
            this.activePath.set(this.activePath().slice(0, columnIndex));
        }
    }

    /** Short preview for an array item — picks the first meaningful scalar
     *  field so the nav row reads as e.g. "[0] Gi0/0/0". */
    summarizeItem(item: any): string {
        if (item === null || item === undefined) return '(empty)';
        if (typeof item !== 'object') return String(item);
        if (Array.isArray(item)) return `${item.length} entries`;
        for (const k of ['name', 'label', 'id', 'key', 'title']) {
            const v = item[k];
            if (v !== undefined && v !== null && v !== '') return String(v);
        }
        for (const k of Object.keys(item)) {
            const v = item[k];
            if (typeof v !== 'object' && v !== null && v !== undefined && v !== '') {
                return `${k}: ${v}`;
            }
        }
        return '(empty)';
    }

    columnPath(col: ColumnDescriptor): string {
        return this.pathToJsonPath(col.path);
    }

    arrayItemPath(col: ColumnDescriptor, index: number): string {
        return this.pathToJsonPath([...col.path, `[${index}]`]);
    }

    private pathToJsonPath(segs: string[]): string {
        let p = '$';
        for (const seg of segs) p += seg.startsWith('[') ? seg : '.' + seg;
        return p;
    }

    private getAtPath(obj: any, path: string[]): any {
        let cur: any = obj;
        for (const seg of path) {
            if (cur === null || cur === undefined) return undefined;
            if (seg.startsWith('[')) {
                const idx = parseInt(seg.slice(1, -1), 10);
                cur = Array.isArray(cur) ? cur[idx] : undefined;
            } else {
                cur = cur[seg];
            }
        }
        return cur;
    }

    private setAtPath(obj: any, path: string[], value: any): void {
        if (!path.length) return;
        let cur: any = obj;
        for (let i = 0; i < path.length - 1; i++) {
            const seg = path[i];
            const nextNeedsArray = path[i + 1]?.startsWith('[');
            if (seg.startsWith('[')) {
                const idx = parseInt(seg.slice(1, -1), 10);
                if (!Array.isArray(cur)) return;
                // Only initialize when the slot is genuinely missing/wrong-
                // type — never overwrite a valid object or array, that's
                // what wipes intermediate containers like `interfaces`.
                if (cur[idx] === undefined || cur[idx] === null || typeof cur[idx] !== 'object') {
                    cur[idx] = nextNeedsArray ? [] : {};
                }
                cur = cur[idx];
            } else {
                if (cur[seg] === undefined || cur[seg] === null || typeof cur[seg] !== 'object') {
                    cur[seg] = nextNeedsArray ? [] : {};
                }
                cur = cur[seg];
            }
        }
        const last = path[path.length - 1];
        if (last.startsWith('[')) {
            const idx = parseInt(last.slice(1, -1), 10);
            if (Array.isArray(cur)) cur[idx] = value;
        } else {
            cur[last] = value;
        }
    }

    private deepClone(v: any): any {
        if (v === null || v === undefined) return {};
        return JSON.parse(JSON.stringify(v));
    }

    private deepEqual(a: any, b: any): boolean {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    /** Strip null / empty-string / empty-object values so the emitted
     *  payload doesn't carry ghost keys. Mirrors SchemaForm.cleanseValue. */
    private cleanseValue(v: any): any {
        if (v === null || v === undefined) return v;
        if (Array.isArray(v)) return v.map(item => this.cleanseValue(item));
        if (typeof v !== 'object') return v;
        const out: any = {};
        for (const k of Object.keys(v)) {
            const cleaned = this.cleanseValue(v[k]);
            if (cleaned === null || cleaned === '' || cleaned === undefined) continue;
            if (typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0) continue;
            out[k] = cleaned;
        }
        return out;
    }

    private scrollColumnsToEnd(): void {
        const cols = this.el.nativeElement.querySelector('.cb-columns') as HTMLElement;
        if (cols) cols.scrollTo({ left: cols.scrollWidth, behavior: 'smooth' });
    }
}

interface ColumnDescriptor {
    schema: any;
    value: any;
    path: string[];
    kind: 'object' | 'array' | 'leaf';
}
